import type { UserFormData } from 'components/forms/user/UserForm';
import { DepartmentBreadcrumbItem, formatDepartmentPath } from 'components/department/DepartmentBreadcrumb';
import { FEATURE_TOGGLE_USER_NFQES_EMAIL } from 'env';

type PermissionTemplate = {
  permissionTemplateId: number;
  slug?: string;
};

type UserAssignments = Pick<UserFormData, 'inOrganizations' | 'inDepartments'>;

interface UserPayloadMapperOptions {
  includeGlobalPermissions?: boolean;
}

type EditableUserResponse = {
  preNominals?: string | null;
  postNominals?: string | null;
  firstName: string;
  lastName: string;
  email: string;
  nfqesEmail?: string | null;
  phoneNumber?: string | null;
  organizationId: number;
  roleId: number;
  globalPermissions?: number[];
  inOrganizations?: {
    organizationId: number;
    organizationName?: string;
    templateId: number;
    permissions: number[];
  }[];
  inDepartments?: {
    departmentId: number;
    departmentName?: string;
    breadcrumbs?: DepartmentBreadcrumbItem[];
    templateId: number;
    permissions: number[];
  }[];
  inheritedDepartments?: {
    departmentId: number;
    departmentName: string;
    breadcrumbs: DepartmentBreadcrumbItem[];
    templateId: number;
    permissions: number[];
    sourceDepartmentId: number;
    sourceDepartmentName: string;
  }[];
};

/**
 * Maps organization and department permission assignments from the user form into API payloads.
 */
function mapScopedAssignments(
  { inOrganizations, inDepartments }: UserAssignments,
  getPermissionTemplate: (idOrSlug: number | string) => PermissionTemplate | undefined,
  getDefaultPermissionTemplate: () => PermissionTemplate
) {
  const mappedInOrganizations = Object.entries(inOrganizations)
    .map(([organizationId, value]) => ({
      organizationId: Number(organizationId),
      templateId:
        getPermissionTemplate(value.templateName)?.permissionTemplateId ??
        getDefaultPermissionTemplate().permissionTemplateId,
      permissions: value.permissions,
      checked: value.checked,
    }))
    .filter(({ checked }) => checked)
    .map(({ organizationId, permissions, templateId }) => ({
      organizationId,
      permissions: permissions ?? [],
      templateId,
    }));

  const mappedInDepartments = Object.entries(inDepartments)
    .map(([departmentId, value]) => ({
      departmentId: Number(departmentId),
      templateId:
        getPermissionTemplate(value.templateName)?.permissionTemplateId ??
        getDefaultPermissionTemplate().permissionTemplateId,
      permissions: value.permissions,
      checked: value.checked,
    }))
    .filter(({ checked }) => checked)
    .map(({ departmentId, permissions, templateId }) => ({
      departmentId,
      permissions: permissions ?? [],
      templateId,
    }));

  return {
    inOrganizations: mappedInOrganizations,
    inDepartments: mappedInDepartments,
  };
}

/**
 * Maps the add-user form state into the create-user API payload.
 */
export function mapUserFormToCreatePayload(
  {
    preNominals,
    postNominals,
    firstName,
    lastName,
    email,
    nfqesEmail,
    password,
    phoneNumber,
    roleId,
    organizationId,
    copyProfilesFromUserId,
    globalPermissions,
    inOrganizations,
    inDepartments,
  }: UserFormData,
  getPermissionTemplate: (idOrSlug: number | string) => PermissionTemplate | undefined,
  getDefaultPermissionTemplate: () => PermissionTemplate,
  { includeGlobalPermissions = true }: UserPayloadMapperOptions = {}
) {
  const mappedAssignments = mapScopedAssignments(
    { inOrganizations, inDepartments },
    getPermissionTemplate,
    getDefaultPermissionTemplate
  );

  const payload = {
    preNominals,
    postNominals,
    firstName,
    lastName,
    email,
    ...(FEATURE_TOGGLE_USER_NFQES_EMAIL ? { nfqesEmail: nfqesEmail?.trim() || null } : {}),
    phoneNumber,
    password,
    roleId: Number(roleId),
    organizationId: Number(organizationId),
    ...mappedAssignments,
    copyProfilesFromUserId: copyProfilesFromUserId ? Number(copyProfilesFromUserId) : undefined,
  };

  if (includeGlobalPermissions) {
    return {
      ...payload,
      globalPermissions,
    };
  }

  return payload;
}

/**
 * Maps the edit-user form state into the update-user API payload.
 */
export function mapUserFormToUpdatePayload(
  {
    preNominals,
    postNominals,
    firstName,
    lastName,
    email,
    nfqesEmail,
    phoneNumber,
    roleId,
    globalPermissions,
    inOrganizations,
    inDepartments,
  }: UserFormData,
  getPermissionTemplate: (idOrSlug: number | string) => PermissionTemplate | undefined,
  getDefaultPermissionTemplate: () => PermissionTemplate,
  { includeGlobalPermissions = true }: UserPayloadMapperOptions = {}
) {
  const mappedAssignments = mapScopedAssignments(
    { inOrganizations, inDepartments },
    getPermissionTemplate,
    getDefaultPermissionTemplate
  );

  const payload = {
    preNominals,
    postNominals,
    firstName,
    lastName,
    email,
    ...(FEATURE_TOGGLE_USER_NFQES_EMAIL ? { nfqesEmail: nfqesEmail?.trim() || null } : {}),
    phoneNumber,
    roleId: Number(roleId),
    ...mappedAssignments,
  };

  if (includeGlobalPermissions) {
    return {
      ...payload,
      globalPermissions,
    };
  }

  return payload;
}

/**
 * Maps the editable user API response into initial values for the user form.
 */
export function mapUserResponseToFormInitialValues(
  data: EditableUserResponse,
  getPermissionTemplate: (idOrSlug: number | string) => PermissionTemplate | undefined
): UserFormData {
  return {
    preNominals: data.preNominals ?? '',
    postNominals: data.postNominals ?? '',
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    nfqesEmail: data.nfqesEmail ?? '',
    phoneNumber: data.phoneNumber ? data.phoneNumber : '',
    organizationId: String(data.organizationId),
    roleId: String(data.roleId),
    globalPermissions: data.globalPermissions ?? [],
    password: '',
    confirmPassword: '',
    inOrganizations: (data.inOrganizations ?? []).reduce(
      (acc, curr) => {
        acc[String(curr.organizationId)] = {
          checked: true,
          permissions: curr.permissions,
          templateName: getPermissionTemplate(curr.templateId)?.slug ?? 'no-template',
          organizationName: curr.organizationName!,
        };
        return acc;
      },
      {} as {
        [organizationId: string]: {
          permissions: number[];
          templateName: string;
          checked: boolean;
          organizationName: string;
        };
      }
    ),
    inDepartments: (data.inDepartments ?? []).reduce(
      (acc, curr) => {
        acc[String(curr.departmentId)] = {
          checked: true,
          permissions: curr.permissions,
          templateName: getPermissionTemplate(curr.templateId)?.slug ?? 'no-template',
          departmentName: curr.departmentName!,
          breadcrumbs: curr.breadcrumbs ?? [],
        };
        return acc;
      },
      {} as {
        [departmentId: string]: {
          permissions: number[];
          templateName: string;
          checked: boolean;
          departmentName: string;
          breadcrumbs: DepartmentBreadcrumbItem[];
        };
      }
    ),
    inheritedDepartments: (data.inheritedDepartments ?? []).map((department) => {
      const sourceIndex = department.breadcrumbs.findIndex(
        ({ departmentId }) => departmentId === department.sourceDepartmentId
      );

      return {
        ...department,
        templateName: getPermissionTemplate(department.templateId)?.slug ?? 'no-template',
        sourceDepartmentPath: formatDepartmentPath(department.breadcrumbs.slice(0, sourceIndex + 1)),
      };
    }),
    copyProfilesFromUserId: undefined,
  };
}
